import { useCallback } from 'react';

import { ANALYTICS_SOURCE, SHARE_CONTEXT_COPY_ACTION_SUBJECT_ID } from '../../analytics.ts';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

import { DEFAULT_SHARE_CONTEXT_LEVEL } from './shareContextPrompt.ts';

export function useShareContextCopyPromptAction(prompt: string): () => void {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();

	return useCallback(() => {
		const report = (
			outcome: 'selected' | 'attempted' | 'succeeded' | 'rejected' | 'unsupported',
		) => {
			try {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: outcome,
						actionSubject: 'shareContextClipboard',
						source: ANALYTICS_SOURCE,
						attributes: { contextLevel: DEFAULT_SHARE_CONTEXT_LEVEL },
					},
				}).fire();
			} catch {
				// Observability must not prevent the clipboard write.
			}
		};
		report('selected');
		const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
		if (typeof clipboard?.writeText !== 'function') {
			report('unsupported');
			return;
		}
		report('attempted');
		try {
			void clipboard.writeText(prompt).then(
				() => {
					report('succeeded');
					try {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: SHARE_CONTEXT_COPY_ACTION_SUBJECT_ID,
								source: ANALYTICS_SOURCE,
								attributes: { contextLevel: DEFAULT_SHARE_CONTEXT_LEVEL },
							},
						}).fire();
					} catch {
						// The write already succeeded; an analytics error is not a clipboard rejection.
					}
				},
				() => report('rejected'),
			);
		} catch {
			report('rejected');
		}
	}, [createAnalyticsEvent, prompt]);
}
